import type {
  RemittanceSource,
  RemittanceStatus,
} from '../../services/api/types.gen';

/**
 * Display strings for remittance status/source. These live outside
 * `RemittanceManager` because the manager imports `RemittanceTableToolbar`, so
 * the toolbar cannot import them back from the manager without a cycle. Both the
 * filter dropdowns and the rendered table cells read from here, keeping one
 * source of truth.
 */
export const STATUS_LABELS: Record<RemittanceStatus, string> = {
  saved: 'Saved',
  completed: 'Completed',
};

export const STATUS_VARIANTS: Record<RemittanceStatus, string> = {
  saved: 'text-amber-700 border-amber-300 bg-amber-50',
  completed: 'text-green-700 border-green-300 bg-green-50',
};

export const SOURCE_LABELS: Record<RemittanceSource, string> = {
  stedi_webhook: 'Received electronically',
  manual_upload: 'Manual Upload',
  manual_entry: 'Manual Entry',
};

export const SOURCE_FILTER_OPTIONS = (
  Object.keys(SOURCE_LABELS) as RemittanceSource[]
).map(v => ({ value: v, label: SOURCE_LABELS[v] }));

/**
 * Reference text for the Remittance Advice Remark Codes Roger can meet today.
 *
 * This is Roger's own lookup, not payer data: the 835 carries MOA/MIA and LQ
 * remark codes as bare codes, so no description ever arrives with a remittance
 * and none is stored. Text is reproduced word for word (whitespace normalised)
 * from the X12/CMS Remittance Advice Remark Code list bundled at
 * `.claude/skills/roger-billing/data/code-lists/rarc-remittance-advice-remark-codes.html`.
 * A code that is not listed here still renders as the bare code.
 */
export const REMARK_CODE_DESCRIPTIONS: Record<string, string> = {
  N115:
    'This decision was based on a Local Coverage Determination (LCD). An LCD ' +
    'provides a guide to assist in determining whether a particular item or ' +
    'service is covered. A copy of this policy is available at ' +
    'www.cms.gov/mcd, or if you do not have web access, you may contact the ' +
    'contractor to request a copy of the LCD.',
  N130:
    'Consult plan benefit documents/guidelines for information about ' +
    'restrictions for this service.',
};
